import { FC, useContext, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";

import { Stack, Shelf, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";
import { Icon, Link, Spinner } from "capitalrx-components-2.0/lib/general";
import { Checkbox, Form, Textbox, useForm } from "capitalrx-components-2.0/lib/forms";

import { getPACaseList } from "@/api";
import { STANDARD_RESPONSIVE_LAYOUT } from "@/constants";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";

import styles from "./PAListForm.module.scss";
import PAResultList from "./PAResultList";

const STATUS_OPTIONS = ["In Progress", "Approved", "Denied"];

export const PAListForm: FC = () => {
  const { Layout } = useResponsive(STANDARD_RESPONSIVE_LAYOUT);
  const form = useForm();
  const { getAuthToken } = useContext(AuthenticationContext);

  const { primary_color } = useContext(ThemeSelectorContext);

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const [caseFilters, setCaseFilters] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(0);

  const isActionFilter = useWatch({
    control: form.control,
    name: "action_required_filter",
  });

  const toggleCaseFilter = (status: string) => {
    setCaseFilters((prev) =>
      prev.includes(status) ? [] : [status],
    );
  };

  useEffect(() => {
    const fetchCases = async () => {
      setLoading(true);
      try {
        const authToken = getAuthToken();

        const requestPayload = {
          is_action_required: isActionFilter || false,
          case_outcome: isActionFilter ? [] : caseFilters,
          query_term: searchTerm || "",
          results_per_page: 10,
          page_number: pageNumber,
        };

        const response = await getPACaseList(requestPayload, authToken);
        setResults(response?.results || []);
      } catch (error) {
        console.error("Failed to fetch cases:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCases();
  }, [caseFilters, isActionFilter, searchTerm, pageNumber]);

  useEffect(() => {
    if (isActionFilter) {
      setCaseFilters([]);
    }
  }, [isActionFilter]);

  return (
    <div className={styles.CaseSearchContainer}>
      <Form form={form}>
        <Stack spacing="small">
          <Textbox
            label="Search cases"
            hideLabel={true}
            instructions="Search by patient name, case ID, member ID, drug name, or date"
            prefix={<Icon name="search" />}
            value={searchTerm}
            onChange={(value) => setSearchTerm(value || "")}
            placeholder="Search"
          />
          <Stack spacing="mini">
            <body className={styles.PageSubtitles}>filter by status</body>
            <Layout spacing="small">
              <Shelf spacing="small">
                {STATUS_OPTIONS.map((status) => (
                  <button
                    key={status}
                    className={`${caseFilters.includes(status) ? styles.StatusFilterActive : styles.StatusFilter} ${isActionFilter ? styles.StatusFilterDisabled : ""}`}
                    onClick={() => toggleCaseFilter(status)}
                    disabled={isActionFilter}
                  >
                    {status}
                  </button>
                ))}
                <Shelf.Spacer />
              </Shelf>
              <Shelf.Spacer />
              <Checkbox.Field
                name="action_required_filter"
                label="Only show cases with Action Required"
                hideLabel
              />
            </Layout>
          </Stack>
          <body className={styles.PageSubtitles}>{`${(caseFilters?.join(", ") || "all")} cases`.toLowerCase()}</body>
          {loading && (
            <Stack className={styles.LoadingContainer} alignment="center">
              <Spinner className={styles.LoadingSpinner} size="large" />
            </Stack>
          )}
          {!loading && results?.length === 0 && searchTerm && (
            <div className={styles.ResultPanel}>
              <Stack spacing="small">
                <Semibold>No Cases Found</Semibold>
                {searchTerm && (
                  <Link style={{ color: primary_color }} to="" onClick={() => setSearchTerm("")}>
                    Clear Search
                  </Link>
                )}
                {caseFilters.length > 0 && (
                  <Link style={{ color: primary_color }} to="" onClick={() => setCaseFilters([])}>Expand search to all statuses</Link>
                )}
                {isActionFilter && (
                  <Link style={{ color: primary_color }} to="" onClick={() => form.reset()}>Expand search to all statuses</Link>
                )}
              </Stack>
            </div>
          )}
          {!loading && results?.length === 0 && !searchTerm && <Body>Provider has no prior authorization cases</Body>}
          {!loading && results && <PAResultList results={results} page={pageNumber} setPage={setPageNumber} />}
        </Stack>
      </Form>
    </div>
  );
};

export default PAListForm;
